/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useMemo, useState } from "react"
import ConfirmStep from "./ConfirmStep"
import InputStep from "./InputStep"
import { useTranslation } from "react-i18next"
import { actions, fetchFerroBarUserDataAsync } from "src/state/xFer"
import { useDispatch } from "react-redux"
import { useAction } from "src/hooks/useAction"
import useXFerRate from "../../hooks/useXFerRate"
import useFerroBarHandlers from "../../hooks/useFerroBarHandlers"
import { FER, XFER } from "src/constants"
import { enqueueToast } from "src/components/Toastify"
import {
  formatNumber,
  sanitizeStringForFN,
} from "src/utils/parseStringToBigNumber"
import { useActiveWeb3React } from "src/hooks"
import Modal from "src/components/Modal"
import TransactionSummary, {
  TransactionSummaryData,
} from "src/components/TransactionSummary"
import { useTransactionModal } from "src/providers/TransactionModalProvider"
import { FixedNumber } from "ethers"

interface Props {
  isUnstaking: boolean
  apr: number
}

const StakeModal = ({ isUnstaking, apr }: Props) => {
  const { t } = useTranslation()
  const [step, setStep] = useState(0)
  const [value, setValue] = useState("")

  const { account } = useActiveWeb3React()
  const closeStakeModal = useAction(actions.setOpenedStakeModal, null)
  const goNext = useCallback(() => setStep(1), [])

  const token = isUnstaking ? XFER : FER
  const rate = useXFerRate()
  const dispatch = useDispatch()

  const { onEnterPool, onLeavePool } = useFerroBarHandlers()
  const { closeTransactionModal } = useTransactionModal()

  const title = useMemo(() => {
    if (step === 0) {
      return isUnstaking ? t("Unstake xFER") : t("Stake FER")
    }
    return isUnstaking ? t("Confirm Unstake xFER") : t("Confirm Stake FER")
  }, [isUnstaking, step, t])

  const converted = isUnstaking
    ? FixedNumber.from(sanitizeStringForFN(value)).mulUnsafe(rate)
    : FixedNumber.from(sanitizeStringForFN(value)).divUnsafe(rate)

  const summary: TransactionSummaryData = [
    {
      label: isUnstaking ? t("Unstake") : t("Stake"),
      token: isUnstaking ? XFER : FER,
      amount: formatNumber(value, 2, 6),
    },
    {
      label: t("Receive"),
      token: isUnstaking ? FER : XFER,
      amount: formatNumber(converted, 2, 6),
    },
  ]

  const handleConfirmClick = async () => {
    try {
      if (isUnstaking) {
        await onLeavePool(value, summary)
      } else {
        await onEnterPool(value, summary)
      }
      if (account) {
        dispatch(fetchFerroBarUserDataAsync(account))
      }
    } catch (e) {
      console.error(e)
      enqueueToast(
        "error",
        isUnstaking
          ? t("Your xFER amount has been reverted")
          : t("Your FER amount has been reverted"),
        t("Transaction Failed"),
      )
    }
    closeTransactionModal()
    closeStakeModal()
  }

  return (
    <Modal open onClose={closeStakeModal} title={title}>
      {step === 0 ? (
        <InputStep
          isUnstaking={isUnstaking}
          rate={rate}
          onDismiss={closeStakeModal}
          token={token}
          apr={apr}
          inputValue={value}
          onConfirm={goNext}
          onInputChange={setValue}
        />
      ) : (
        <ConfirmStep
          isUnstaking={isUnstaking}
          inputValue={value}
          rate={rate}
          remark={
            isUnstaking
              ? t(
                  "By converting your xFER back to FER, you will stop earning the APR displayed above accrued from the FER platform buybacks.",
                )
              : t(
                  "You will stake FER to receive xFER. xFER is not tradable and increases value as you will be entitled to more FER over time from platform buyback.",
                )
          }
          onDismiss={closeStakeModal}
          onConfirm={handleConfirmClick}
          earningToken={isUnstaking ? FER : XFER}
          stakingToken={isUnstaking ? XFER : FER}
          apr={apr}
        />
      )}
    </Modal>
  )
}

export default StakeModal
